import { useMemo, useCallback } from 'react';
import { useAppThunkDispatch } from 'state/store';
import { useSelector } from 'react-redux';
import {
  getProcessesList,
  selectProcesses,
  selectSelectedProcessIds,
  selectSelectedProcesses,
  setSelectedProcessIds,
} from 'state/ProjectsList/filters/Processes/processesSlice';
import { ProjectProcess } from 'utils/types/projectsList';

interface UseProjectProcessesParams {
  appliedFilters?: string[];
}

interface UseProjectProcessesReturn {
  fetchProcesses: () => Promise<void>;
  fetchSelectedProcesses: (selectedProcessIds: string[]) => Promise<void>;
  processes: ProjectProcess[];
  selectedProcessIds: string[];
  shouldFetchProcesses: boolean;
  selectedProcesses: ProjectProcess[];
}

export const useProjectProcesses = ({
  appliedFilters,
}: UseProjectProcessesParams): UseProjectProcessesReturn => {
  const dispatch = useAppThunkDispatch();
  const processes = useSelector(selectProcesses);
  const selectedProcessIds = useSelector(selectSelectedProcessIds);
  const selectedProcesses = useSelector(selectSelectedProcesses);

  const fetchProcesses = useCallback(async () => {
    await dispatch(getProcessesList());
  }, [dispatch]);

  const fetchSelectedProcesses = useCallback(
    async (selectedProcessIds: string[]) => {
      await dispatch(setSelectedProcessIds(selectedProcessIds));
      await fetchProcesses();
    },
    [dispatch, fetchProcesses]
  );

  const shouldFetchProcesses = useMemo(() => {
    if (appliedFilters && appliedFilters.length > 0) {
      return (
        selectedProcesses.length === 0 ||
        appliedFilters.length !== selectedProcessIds.length
      );
    }
    return processes.length === 0;
  }, [
    appliedFilters,
    processes.length,
    selectedProcessIds.length,
    selectedProcesses.length,
  ]);

  return useMemo(() => {
    return {
      fetchProcesses,
      fetchSelectedProcesses,
      processes,
      selectedProcessIds,
      shouldFetchProcesses,
      selectedProcesses,
    };
  }, [
    fetchProcesses,
    fetchSelectedProcesses,
    processes,
    selectedProcessIds,
    shouldFetchProcesses,
    selectedProcesses,
  ]);
};
